import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { HeartHandshake, Download, TrendingUp, Users, ClipboardList, Repeat, XCircle, PlusCircle } from "lucide-react";

const brl = (n) => Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return "—"; } };

/**
 * Central Financeira do Filho Presente — separada do RAF Direct.
 * Mostra MRR, ARR, assinaturas ativas, novas/canceladas no período e
 * solicitações (indicador operacional).
 */
const AdminFinanceFPPanel = () => {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(90);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/finance/fp?days=${days}`);
      setD(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar finanças FP");
    }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    try {
      const r = await api.get(`/admin/finance/fp.csv?days=${days}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `filho_presente_finance_${days}d.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV FP exportado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao exportar");
    }
  };

  if (!d) return <div className="p-6 text-slate-500 text-sm">Carregando finanças Filho Presente…</div>;
  const k = d.kpis;

  return (
    <div className="space-y-5" data-testid="admin-finance-fp-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <HeartHandshake className="w-5 h-5 text-amber-600" />
            <h2 className="font-display font-bold text-xl text-[#0B2B62]">Central Financeira · Filho Presente</h2>
          </div>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            MRR, ARR e assinaturas ativas dos planos <b>Essencial</b> (R$ 99,90/mês) e <b>Cuidado Total</b> (R$ 199,90/mês).
            Separado do RAF Direct para acompanhar cada produto sem misturar.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="flex gap-1 text-xs">
            {[[30,"30d"],[90,"90d"],[180,"6m"],[365,"1a"]].map(([v,lbl]) => (
              <button key={v} onClick={() => setDays(v)}
                data-testid={`fp-finance-range-${v}`}
                className={`px-3 py-1.5 rounded-md border ${days === v ? "bg-amber-600 text-white border-amber-600" : "text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                {lbl}
              </button>
            ))}
          </div>
          <button onClick={downloadCsv}
            data-testid="fp-finance-export-csv"
            className="text-xs px-3 py-1.5 bg-amber-600 text-white rounded-md font-semibold hover:bg-amber-700 inline-flex items-center gap-1.5">
            <Download className="w-3.5 h-3.5" /> Exportar CSV
          </button>
        </div>
      </div>

      {/* KPIs principais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="MRR" value={brl(k.mrr)} icon={TrendingUp} accent="amber"
          hint="Receita mensal recorrente (mensal + anual/12)" testId="fp-kpi-mrr" />
        <KPI label="ARR (projeção 12m)" value={brl(k.arr)} icon={Repeat} accent="emerald"
          hint="MRR × 12" testId="fp-kpi-arr" />
        <KPI label="Assinaturas ativas" value={k.active_subscriptions} icon={Users}
          testId="fp-kpi-active" />
        <KPI label="Beneficiários ativos" value={k.active_beneficiaries} icon={HeartHandshake} accent="rose"
          testId="fp-kpi-benef" />
      </div>

      {/* Movimentação no período */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label={`Novas (${days}d)`} value={k.new_in_period} icon={PlusCircle} accent="emerald"
          testId="fp-kpi-new" />
        <KPI label={`Canceladas (${days}d)`} value={k.cancelled_in_period} icon={XCircle} accent="red"
          testId="fp-kpi-cancel" />
        <KPI label={`Solicitações (${days}d)`} value={k.requests_in_period} icon={ClipboardList}
          testId="fp-kpi-requests" />
        <KPI label="Famílias com plano" value={k.families_with_plan} icon={Users} accent="blue"
          testId="fp-kpi-families" />
      </div>

      {/* Breakdown por plano+ciclo */}
      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="fp-plans-table">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div className="text-sm font-bold text-[#0B2B62]">Assinaturas ativas por plano</div>
          <div className="text-xs text-slate-500 mt-0.5">Anual conta como 1/12 do valor no MRR.</div>
        </div>
        {d.plans.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">Ainda não há assinaturas ativas.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              <tr>
                <th className="text-left px-4 py-2">Plano</th>
                <th className="text-left px-4 py-2">Ciclo</th>
                <th className="text-right px-4 py-2">Assinantes</th>
                <th className="text-right px-4 py-2">Preço/mês equivalente</th>
                <th className="text-right px-4 py-2">Receita mensal</th>
              </tr>
            </thead>
            <tbody>
              {d.plans.map((r) => (
                <tr key={`${r.plan}_${r.cycle}`} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-bold text-[#0B2B62]">{r.plan_label}</td>
                  <td className="px-4 py-2 text-slate-600 capitalize">{r.cycle === "yearly" ? "Anual" : "Mensal"}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.count}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{brl(r.monthly_price)}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-bold text-amber-700">{brl(r.monthly_equiv_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Solicitações por tipo */}
      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="fp-requests-table">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div className="text-sm font-bold text-[#0B2B62]">Solicitações no período — {days}d</div>
          <div className="text-xs text-slate-500 mt-0.5">Não gera receita direta; mede engajamento do produto.</div>
        </div>
        {d.requests_by_type.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">Sem solicitações no período.</div>
        ) : (
          <div className="p-3 flex flex-wrap gap-2">
            {d.requests_by_type.map((r) => (
              <span key={r.type}
                className="inline-flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 rounded-full px-3 py-1 text-amber-800 font-semibold uppercase tracking-widest">
                {r.type}
                <span className="bg-amber-600 text-white rounded-full px-2 tabular-nums">{r.count}</span>
              </span>
            ))}
          </div>
        )}
      </section>

      {/* Assinaturas recentes */}
      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="fp-recent-table">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div className="text-sm font-bold text-[#0B2B62]">Assinaturas recentes</div>
        </div>
        {d.recent.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">Nenhuma assinatura nova no período.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              <tr>
                <th className="text-left px-4 py-2">Família</th>
                <th className="text-left px-4 py-2">Plano</th>
                <th className="text-left px-4 py-2">Ciclo</th>
                <th className="text-right px-4 py-2">Valor/mês</th>
                <th className="text-right px-4 py-2">Início</th>
              </tr>
            </thead>
            <tbody>
              {d.recent.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-4 py-2">{r.family_display}</td>
                  <td className="px-4 py-2 font-bold text-[#0B2B62]">{r.plan_label}</td>
                  <td className="px-4 py-2 text-slate-600 capitalize">{r.cycle === "yearly" ? "Anual" : "Mensal"}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{brl(r.amount_monthly)}</td>
                  <td className="px-4 py-2 text-right text-slate-500 text-xs">{fmt(r.started_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

const KPI = ({ label, value, icon: Icon, accent, hint, testId }) => {
  const map = {
    amber: "border-amber-300 bg-amber-50 text-amber-800",
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-800",
    blue: "border-blue-300 bg-blue-50 text-blue-800",
    rose: "border-rose-300 bg-rose-50 text-rose-800",
    red: "border-red-300 bg-red-50 text-red-800",
  };
  return (
    <div className={`p-3 border rounded-lg ${map[accent] || "border-slate-200 bg-white text-[#0B2B62]"}`}
      data-testid={testId}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 opacity-80" />}
        <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80">{label}</div>
      </div>
      <div className="font-display font-bold text-2xl mt-1 tabular-nums">{value}</div>
      {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
};

export default AdminFinanceFPPanel;
